/**
 * @fileoverview Connect lead evidence documents to explicit upload/request actions and draft context. Document, outreach, and engagement-draft reads have independent backend ownership. `DriveDocumentPreview.js` owns read-only evidence selection; `EngagementDraftPreview.js` owns draft editing. The tab coordinates affected refreshes without treating a displayed document request as a sent message or an uploaded file as a reviewed decision.
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import ToastContext from '../../ToastContext';
import { fetchIntakeDocuments, fetchIntakeEngagementDraft, fetchIntakeOutreach, uploadIntakeDocuments } from './intakeApi';
import { RecordList } from './IntakeDetailPrimitives';
import DriveDocumentPreview from './DriveDocumentPreview';
import EngagementDraftPreview from './EngagementDraftPreview';
import { formatDate, titleize } from './intakeFormatters';
import './IntakeDocumentsTab.css';

// Documents tab: Drive-synced and uploaded documents for a lead, with review-status
// labels and upload handling.



function getDocumentSubtitle(row) {
  if (row.review_notes) return row.review_notes;
  if (row.review_status === 'reviewed' && row.reviewed_at) {
    const prefix = String(row.reviewed_by_actor_id || '').startsWith('intake_agent_run:') ? 'Agent reviewed' : 'Reviewed';
    return `${prefix} ${formatDate(row.reviewed_at)}`;
  }
  return formatDate(row.created_at);
}



function getDriveFolderUrl(folderId) {
  return folderId ? `https://drive.google.com/drive/folders/${folderId}` : '';
}



function getDriveFileUrl(fileId) {
  return fileId ? `https://drive.google.com/file/d/${fileId}/view` : '';
}



function getDocumentHref(row) {
  return row.file_url || getDriveFileUrl(row.google_drive_file_id);
}



function getDocumentTitle(row) {
  return row.title || row.storage_key || row.file_url || row.google_drive_file_id || 'Untitled document';
}



function getDocumentMeta(row) {
  const parts = [];
  if (row.source_system) parts.push(titleize(row.source_system));
  if (row.mime_type || row.document_type) parts.push(row.mime_type || row.document_type);
  if (row.google_drive_folder_id) parts.push(`Folder ${row.google_drive_folder_id}`);
  return parts.join(' / ') || 'Document';
}



function getDriveSyncLabel(driveSync) {
  if (!driveSync) return 'Drive folder not checked yet.';
  if (driveSync.status === 'synced') {
    const count = Number(driveSync.documents_linked || 0) + Number(driveSync.documents_updated || 0);
    return count > 0 ? `${count} Drive document ${count === 1 ? 'loaded' : 'updates loaded'}.` : 'Drive folder is current.';
  }
  if (driveSync.status === 'not_found') return driveSync.folder_name ? `No Drive folder found for ${driveSync.folder_name}.` : 'No Drive folder found for this lead.';
  if (driveSync.status === 'skipped') return driveSync.reason || 'Drive refresh skipped.';
  if (driveSync.status === 'error') return 'Drive refresh failed; showing saved document records.';
  return 'Drive folder checked.';
}



function getDriveDocumentCount(driveSync) {
  if (!driveSync) return null;
  let rawCount = driveSync.drive_document_count;
  if (rawCount == null) rawCount = driveSync.folder_document_count;
  if (rawCount == null) rawCount = driveSync.document_count;
  const count = Number(rawCount);
  return Number.isFinite(count) && count >= 0 ? count : null;
}



function getDriveDocumentCountLabel(driveSync) {
  const count = getDriveDocumentCount(driveSync);
  if (count === null) return null;
  return `${count} document${count === 1 ? '' : 's'} in folder`;
}



function mergeDriveSyncAfterUpload(current, next, uploadedCount) {
  if (!next) return current;
  const nextCount = getDriveDocumentCount(next);
  if (nextCount !== null) return { ...next, drive_document_count: nextCount };
  const currentCount = getDriveDocumentCount(current);
  if (currentCount === null) return { ...(current || {}), ...next };
  return {
    ...(current || {}),
    ...next,
    drive_document_count: currentCount + Math.max(Number(uploadedCount) || 0, 0),
  };
}

const DOCUMENT_COORDINATION_STAGE_KEYS = new Set([
  'info_gathering',
  'information_request',
  'outreach_response_monitor',
  'document_review',
  'information_evaluation',
]);



export function isDocumentFollowUpRelevant(lead) {
  if (!lead || ['accepted', 'rejected', 'converted', 'duplicate', 'archived'].includes(lead.status)) return false;
  return DOCUMENT_COORDINATION_STAGE_KEYS.has(lead.current_stage);
}



function getPreparedOutreach(rows) {
  return (rows || []).find((row) => ['draft', 'pending', 'ready_for_review'].includes(String(row.status || '').toLowerCase())) || null;
}



function DocumentFollowUpPanel({ lead, documents, outreachRows }) {
  const [prototypeState, setPrototypeState] = useState('');
  const [copied, setCopied] = useState(false);
  const preparedOutreach = getPreparedOutreach(outreachRows);
  const activeDocuments = documents.filter((row) => row.status !== 'archived');
  const pendingReviewCount = activeDocuments.filter((row) => row.review_status === 'pending').length;
  const requestRecorded = Boolean(lead?.information_request_monitoring?.has_request || outreachRows.length);
  const nextFollowUp = lead?.next_action_at ? formatDate(lead.next_action_at) : 'No follow-up date set';
  const missingStatus = lead?.status === 'waiting_on_prospect' ?
    'Waiting on prospect' :
    pendingReviewCount > 0 ? 'Completeness not confirmed' : 'No missing list recorded';

  useEffect(() => {
    setPrototypeState('');
    setCopied(false);
  }, [lead?.id]);

  if (!isDocumentFollowUpRelevant(lead)) return null;

  const copyPreparedRequest =

  async () => {
    if (!preparedOutreach?.body) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(preparedOutreach.body);
      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <section className="intake-document-follow-up" aria-label="Document Follow-up">
      <header className="intake-document-follow-up__header">
        <div>
          <span className="intake-document-follow-up__eyebrow">Coordination support</span>
          <h2>Document Follow-up</h2>
          <p>Tracks the existing request and document-review flow. It does not make a legal decision.</p>
        </div>
        <span className="intake-document-follow-up__prototype">Local prototype · not saved or synced</span>
      </header>

      <div className="intake-document-follow-up__scan">
        <div><span>Required</span><strong>{requestRecorded ? 'Request recorded' : 'Checklist not recorded'}</strong></div>
        <div><span>Received</span><strong>{activeDocuments.length} document{activeDocuments.length === 1 ? '' : 's'}</strong></div>
        <div><span>Missing</span><strong>{missingStatus}</strong></div>
        <div><span>Next follow-up</span><strong>{nextFollowUp}</strong></div>
      </div>

      <div className="intake-document-follow-up__next">
        <div>
          <span>Current coordination step</span>
          <strong>{lead?.next_action_label || (pendingReviewCount > 0 ? 'Review pending documents in the existing stage' : 'Confirm whether follow-up is needed')}</strong>
        </div>
        <span>{pendingReviewCount} pending review</span>
      </div>

      {preparedOutreach ? <details className="intake-document-follow-up__draft">
        <summary>Prepared request or reminder</summary>
        <div>
          <strong>{preparedOutreach.subject || 'Prepared outreach'}</strong>
          <p>{preparedOutreach.body || 'The saved draft has no message body.'}</p>
          <button type="button" className="intake-button" disabled={!preparedOutreach.body} onClick={copyPreparedRequest}>
            <AppIcon icon={copied ? 'pi pi-check' : 'pi pi-copy'} aria-hidden="true" />
            <span>{copied ? 'Copied' : 'Copy draft'}</span>
          </button>
        </div>
      </details> : null}

      <div className="intake-document-follow-up__actions" aria-label="Prototype document follow-up actions">
        <button type="button" className="intake-button" onClick={() => setPrototypeState('Received recorded locally')}>
          <AppIcon icon="pi pi-inbox" aria-hidden="true" /><span>Mark received</span>
        </button>
        <button type="button" className="intake-button" onClick={() => setPrototypeState('Still waiting recorded locally')}>
          <AppIcon icon="pi pi-clock" aria-hidden="true" /><span>Still waiting</span>
        </button>
        <button type="button" className="intake-button" onClick={() => setPrototypeState('Attorney handoff prepared locally')}>
          <AppIcon icon="pi pi-briefcase" aria-hidden="true" /><span>Ready for attorney review</span>
        </button>
        <button type="button" className="intake-button" onClick={() => setPrototypeState('Exception flagged locally')}>
          <AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" /><span>Escalate exception</span>
        </button>
      </div>
      {prototypeState ? <p className="intake-document-follow-up__confirmation">{prototypeState}. No Intake or Pipedrive record was changed.</p> : null}
    </section>
  );
}



export function DocumentsTab({ lead, active, onRefreshLead }) {
  const showToast = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [driveSync, setDriveSync] = useState(null);
  const [outreachRows, setOutreachRows] = useState([]);
  const [engagementDraft, setEngagementDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const loadRows = useCallback(

  async () => {
    setLoading(true);
    try {
      const [documentsResult, outreachResult, engagementResult] = await Promise.allSettled([
        fetchIntakeDocuments(lead.id),
        isDocumentFollowUpRelevant(lead) ? fetchIntakeOutreach(lead.id) : Promise.resolve({ data: { outreach: [] } }),
        fetchIntakeEngagementDraft(lead.id),
      ]);
      if (documentsResult.status === 'rejected') throw documentsResult.reason;
      setRows(documentsResult.value.data.documents || []);
      setDriveSync(documentsResult.value.data.drive_sync || null);
      setOutreachRows(outreachResult.status === 'fulfilled' ? outreachResult.value.data.outreach || [] : []);
      setEngagementDraft(engagementResult.status === 'fulfilled' ? engagementResult.value.data.engagement_draft || null : null);
      setLoaded(true);
    } catch (error) {
      console.error('Failed to load intake documents:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load documents' });
    } finally {
      setLoading(false);
    }
  }, [lead, showToast]);

  useEffect(

  () => {
    setRows([]);
    setDriveSync(null);
    setOutreachRows([]);
    setEngagementDraft(null);
    setLoaded(false);
  }, [lead.id]);

  useEffect(() => {
    if (active && !loaded) loadRows();
  }, [active, loaded, loadRows]);

  const uploadFiles =

  async (fileList) => {
    const files = Array.from(fileList || []).filter((file) => file?.name);
    if (!files.length) return;
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    setUploading(true);
    try {
      const response = await uploadIntakeDocuments(lead.id, formData);
      const uploaded = response.data.documents || [];
      setRows((current) => {
        const uploadedIds = new Set(uploaded.map((row) => row.id));
        return uploaded.concat(current.filter((row) => !uploadedIds.has(row.id)));
      });
      if (response.data.drive_sync) {
        setDriveSync((current) => mergeDriveSyncAfterUpload(current, response.data.drive_sync, uploaded.length || files.length));
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onRefreshLead) await onRefreshLead();
      showToast({
        severity: 'success',
        summary: 'Uploaded',
        detail: `${uploaded.length || files.length} document${(uploaded.length || files.length) === 1 ? '' : 's'} uploaded to Drive`,
      });
    } catch (error) {
      console.error('Failed to upload intake documents:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to upload documents' });
    } finally {
      setUploading(false);
      setDragActive(false);
    }
  };

  const handleDrop =

  (event) => {
    event.preventDefault();
    event.stopPropagation();
    uploadFiles(event.dataTransfer.files);
  };

  const driveFolderUrl = driveSync?.folder_url || getDriveFolderUrl(driveSync?.folder_id);
  const driveDocumentCountLabel = getDriveDocumentCountLabel(driveSync);

  return (
    <div className="intake-tab-panel">
            <DocumentFollowUpPanel lead={lead} documents={rows} outreachRows={outreachRows} />
            {engagementDraft && (
              <section className="intake-detail-panel">
                <EngagementDraftPreview draft={engagementDraft} onRefresh={loadRows} />
              </section>
            )}
            <section
              className={`intake-document-dropzone${dragActive ? ' intake-document-dropzone--active' : ''}${uploading ? ' intake-document-dropzone--uploading' : ''}`}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  aria-label="Upload documents to Google Drive"
                  className="intake-document-dropzone__input"
                  onChange={(event) => uploadFiles(event.target.files)}
                />
                <div className="intake-document-dropzone__content">
                    <AppIcon icon={uploading ? 'pi pi-spin pi-spinner' : 'pi pi-cloud-upload'} aria-hidden="true" />
                    <div>
                        <strong>{uploading ? 'Uploading to Google Drive' : 'Drop documents here'}</strong>
                        <span>Files are saved to this lead's Google Drive folder and added to the document list.</span>
                    </div>
                </div>
                <button type="button" className="intake-button intake-button--primary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                    <AppIcon icon="pi pi-upload" aria-hidden="true" />
                    <span>{uploading ? 'Uploading' : 'Choose Files'}</span>
                </button>
            </section>
            <div className="intake-documents-panel">
                <div className="intake-drive-sync-panel">
                    <div className="intake-drive-sync-panel__summary">
                        <div className="intake-drive-sync-panel__heading">
                            <strong>Google Drive Folder</strong>
                            {driveDocumentCountLabel && <span className="intake-drive-sync-panel__count">{driveDocumentCountLabel}</span>}
                        </div>
                        <span className="intake-drive-sync-panel__status">{getDriveSyncLabel(driveSync)}</span>
                    </div>
                    <div className="intake-record-row__actions">
                        {driveFolderUrl &&
                        <a className="intake-button" href={driveFolderUrl} target="_blank" rel="noreferrer">
                            <AppIcon icon="pi pi-folder-open" aria-hidden="true" />
                            <span>Open Folder</span>
                        </a>}
                        <button type="button" className="intake-button" disabled={loading} onClick={() => loadRows()}>
                            <AppIcon icon="pi pi-refresh" aria-hidden="true" />
                            <span>{loading ? 'Refreshing' : 'Refresh'}</span>
                        </button>
                    </div>
                </div>
                <RecordList title="Documents" loading={loading} empty="No documents in the lead Drive folder yet.">
                    <DriveDocumentPreview documents={rows} getHref={getDocumentHref} getTitle={getDocumentTitle} getMeta={(row) => [getDocumentMeta(row), row.review_status === 'pending' ? 'Pending Review' : getDocumentSubtitle(row)].filter(Boolean).join(' · ')} empty="No documents in the lead Drive folder yet." />
                </RecordList>
            </div>
        </div>);

}
